import React from "react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Heart, Building2, Users } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurDonorEcosystem() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const corporatePartners = {
    platinum: [{
      name: "Global Tech Solutions",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/global_tech_logo.png"
    }, {
      name: "First National Bank",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/first_national_bank_logo.png"
    }, {
      name: "Energy Colorado",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/energy_colorado_logo.png"
    }],
    gold: [{
      name: "Denver Health",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/denver_health_logo.png"
    }, {
      name: "Western Union",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/western_union_logo.png"
    }, {
      name: "Comcast NBCUniversal",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/comcast_logo.png"
    }, {
      name: "Target Corporation",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/target_logo.png"
    }],
    silver: ["Alpine Bank", "Xcel Energy", "Southwest Airlines", "Kroger Co.", "Wells Fargo", "PNC Bank", "State Farm", "Verizon"]
  };
  const foundationSupporters = {
    major: [{
      name: "The Colorado Health Foundation",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/colorado_health_foundation_logo.png"
    }, {
      name: "Daniels Fund",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/daniels_fund_logo.png"
    }, {
      name: "Boettcher Foundation",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/boettcher_foundation_logo.png"
    }, {
      name: "Gates Family Foundation",
      logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/gates_family_foundation_logo.png"
    }],
    community: ["Rose Community Foundation", "The Denver Foundation", "Latino Community Foundation of Colorado", "Caring for Colorado", "Anschutz Family Foundation", "El Pomar Foundation"]
  };
  const individualDonors = {
    visionary: ["Maria & Carlos Gonzalez", "The Rodriguez Family Trust", "Dr. Elena Martinez", "Robert & Linda Smith"],
    champions: ["Alejandro Rivera", "Sarah Johnson", "Miguel Angel Torres", "Emily White", "David & Patricia Lopez", "James O'Connor", "Sofia Ramirez", "Michael Brown", "Isabella Garcia", "William Davis", "Camila Hernandez", "Daniel Wilson"]
  };
  return <section id="our-donor-ecosystem" ref={ref} className={`py-24 bg-background relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-accent/30 rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-primary/5 rounded-full blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="mb-4 border-secondary text-secondary-foreground bg-secondary/10">
            Our Supporters
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-primary mb-6">
            Our Donor Ecosystem
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Celebrating the visionary partners, foundations, and individuals who power our mission to expand economic opportunity for Colorado’s Hispanic community.
          </p>
        </div>

        <Tabs defaultValue="corporate" className="w-full flex flex-col items-center">
          <TabsList className="mb-12 bg-muted/50 p-1 h-auto flex-wrap justify-center gap-2 rounded-full border border-border/50">
            <TabsTrigger value="corporate" className="rounded-full px-6 py-3 text-base data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all duration-300">
              <Building2 className="w-4 h-4 mr-2" />
              Corporate Partners
            </TabsTrigger>
            <TabsTrigger value="foundation" className="rounded-full px-6 py-3 text-base data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all duration-300">
              <Heart className="w-4 h-4 mr-2" />
              Foundation Supporters
            </TabsTrigger>
            <TabsTrigger value="individual" className="rounded-full px-6 py-3 text-base data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all duration-300">
              <Users className="w-4 h-4 mr-2" />
              Individual Donors
            </TabsTrigger>
          </TabsList>

          {}
          <TabsContent value="corporate" className="w-full space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {}
            <div className="space-y-8">
              <div className="flex items-center justify-center gap-4">
                <div className="h-px bg-gradient-to-r from-transparent via-secondary to-transparent w-24 opacity-50" />
                <h3 className="text-2xl font-serif font-bold text-primary text-center">Platinum Partners</h3>
                <div className="h-px bg-gradient-to-r from-transparent via-secondary to-transparent w-24 opacity-50" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {corporatePartners.platinum.map((partner, i) => <Card key={i} data-index={i} className="group relative bg-white border-secondary/20 overflow-hidden hover:-translate-y-1 transition-all duration-300 shadow-[0_0_20px_-5px_rgba(234,179,8,0.15)] hover:shadow-[0_0_30px_-5px_rgba(234,179,8,0.4)]">
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <CardContent className="p-10 flex items-center justify-center h-48">
                      <img src={partner.logo} alt={`${partner.name} logo`} className="max-h-24 w-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 transform group-hover:scale-105" />
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            {}
            <div className="space-y-8">
              <h3 className="text-xl font-serif font-semibold text-primary/80 text-center">Gold Partners</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                {corporatePartners.gold.map((partner, i) => <div key={i} data-index={i} className="bg-white rounded-xl p-6 flex items-center justify-center h-32 border border-border shadow-sm hover:shadow-md transition-all duration-300 hover:border-secondary/30">
                    <img src={partner.logo} alt={`${partner.name} logo`} className="max-h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
                  </div>)}
              </div>
            </div>

            {}
            <div className="space-y-6 bg-muted/30 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto border border-border/50">
              <h3 className="text-lg font-serif font-semibold text-primary/70 text-center mb-6">Silver Partners</h3>
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-center">
                {corporatePartners.silver.map((name, i) => <span key={i} data-index={i} className="text-muted-foreground font-medium hover:text-primary transition-colors duration-200">
                    {name}
                  </span>)}
              </div>
            </div>
          </TabsContent>

          {}
          <TabsContent value="foundation" className="w-full space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-10">
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-serif font-bold text-primary">Strategic Grantmakers</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">Foundations providing critical multi-year support for our core programs.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                {foundationSupporters.major.map((foundation, i) => <Card key={i} data-index={i} className="bg-white border-border hover:border-primary/20 transition-all duration-300 hover:shadow-lg group">
                    <CardContent className="p-8 flex items-center justify-center h-40">
                      <img src={foundation.logo} alt={`${foundation.name} logo`} className="max-h-16 w-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300" />
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h3 className="text-xl font-serif font-semibold text-primary">Community Impact Funds</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {foundationSupporters.community.map((name, i) => <div key={i} data-index={i} className="bg-white/50 border border-border/60 rounded-lg p-4 text-sm font-medium text-primary/80 hover:bg-white hover:shadow-sm transition-all">
                    {name}
                  </div>)}
              </div>
            </div>
          </TabsContent>

          {}
          <TabsContent value="individual" className="w-full space-y-16 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {}
            <div className="text-center space-y-10">
              <div className="inline-block relative">
                <h3 className="text-3xl font-serif font-bold text-primary relative z-10 px-4">The Visionary Circle</h3>
                <div className="absolute inset-0 bg-secondary/10 -skew-x-12 transform scale-110 z-0 rounded-sm" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {individualDonors.visionary.map((name, i) => <div key={i} data-index={i} className="flex items-center justify-center p-6 bg-white shadow-sm border-t-4 border-secondary rounded-lg">
                    <span className="text-xl md:text-2xl font-serif text-primary">{name}</span>
                  </div>)}
              </div>
            </div>

            {}
            <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-border p-8 md:p-12">
              <h3 className="text-xl font-serif font-semibold text-primary mb-8 text-center border-b border-border pb-4">
                Community Builders
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-4 gap-x-8">
                {individualDonors.champions.map((name, i) => <li key={i} data-index={i} className="flex items-center text-muted-foreground hover:text-primary transition-colors">
                    <span className="w-1.5 h-1.5 bg-secondary rounded-full mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base">{name}</span>
                  </li>)}
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-20 text-center">
          <div className="inline-flex flex-col items-center bg-primary/5 rounded-2xl p-8 md:p-10 border border-primary/10 max-w-2xl mx-auto">
            <h4 className="text-2xl font-serif font-bold text-primary mb-3">Join Our Mission</h4>
            <p className="text-muted-foreground mb-6 max-w-md">
              Your support empowers Hispanic entrepreneurs and leaders across Colorado. Become a partner today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold px-8">
                <Link to="/GetInvolvedPage">
                  Become a Donor
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/5">
                <Link to="/PartnersPage">
                  View Partnership Deck <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}